import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Search,
    Sparkles,
    Send,
    LineChart,
    Bot,
    Plug,
    Wallet,
    Users,
    Settings,
    Upload,
    Rocket,
} from "lucide-react";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";

export default function CommandPalette({ open, onOpenChange }) {
    const navigate = useNavigate();
    const { t } = useI18n();

    useEffect(() => {
        const down = (e) => {
            if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onOpenChange(!open);
            }
        };
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, [open, onOpenChange]);

    function go(path) {
        onOpenChange(false);
        navigate(path);
    }

    const nav = [
        { key: "dashboard", label: t.nav.dashboard, icon: LayoutDashboard, to: "/dashboard" },
        { key: "find", label: t.nav.find, icon: Search, to: "/app/find" },
        { key: "enrich", label: t.nav.enrich, icon: Sparkles, to: "/app/enrich" },
        { key: "engage", label: t.nav.engage, icon: Send, to: "/app/engage" },
        { key: "analyze", label: t.nav.analyze, icon: LineChart, to: "/app/analyze" },
        { key: "copilot", label: t.nav.copilot, icon: Bot, to: "/app/copilot" },
    ];
    const workspace = [
        { key: "integrations", label: t.nav.integrations, icon: Plug, to: "/app/integrations" },
        { key: "team", label: t.nav.team, icon: Users, to: "/app/team" },
        { key: "billing", label: t.nav.billing, icon: Wallet, to: "/app/billing" },
        { key: "settings", label: t.nav.settings, icon: Settings, to: "/app/settings" },
    ];
    const actions = [
        { key: "action-prospect", label: t.app.actionProspect, icon: Rocket, to: "/app/find" },
        { key: "action-import", label: t.app.actionImport, icon: Upload, to: "/app/find" },
        { key: "action-campaign", label: t.app.actionCampaign, icon: Send, to: "/app/engage" },
    ];

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange}>
            <div data-testid={TEST_IDS.app.cmdkPalette}>
                <CommandInput placeholder={t.app.searchHint} data-testid={TEST_IDS.app.cmdkInput} />
                <CommandList className="max-h-[420px]">
                    <CommandEmpty>{t.cmdk.empty}</CommandEmpty>
                    <CommandGroup heading={t.cmdk.heading}>
                        {nav.map(({ key, label, icon: Icon, to }) => (
                            <CommandItem
                                key={key}
                                data-testid={TEST_IDS.app.cmdkItem(key)}
                                onSelect={() => go(to)}
                                className="gap-2"
                            >
                                <Icon size={14} className="text-brand-cyan" />
                                <span>{label}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup heading={t.cmdk.actions}>
                        {actions.map(({ key, label, icon: Icon, to }) => (
                            <CommandItem
                                key={key}
                                data-testid={TEST_IDS.app.cmdkItem(key)}
                                onSelect={() => go(to)}
                                className="gap-2"
                            >
                                <Icon size={14} className="text-brand-violet" />
                                <span>{label}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup heading="Workspace">
                        {workspace.map(({ key, label, icon: Icon, to }) => (
                            <CommandItem
                                key={key}
                                data-testid={TEST_IDS.app.cmdkItem(key)}
                                onSelect={() => go(to)}
                                className="gap-2"
                            >
                                <Icon size={14} className="text-zinc-400" />
                                <span>{label}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                </CommandList>
            </div>
        </CommandDialog>
    );
}
